import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import Button from '../../components/form/button/Button';
import TBody from '../../components/table/TBody/TBody';
import TD from '../../components/table/TD/TD';
import TH from '../../components/table/TH/TH';
import THead from '../../components/table/THead/THead';
import TR from '../../components/table/TR/TR';
import Table from '../../components/table/Table';
import TableWrapper from '../components/table-wrapper/TableWrapper';
import { RootState } from '../../core/state/state';
import { getExternalUsers } from '../../core/state/users/asyncActions';
import Icon from '../../components/icon/Icon';
import { DATE_FORMAT_EU_DOT } from '../../core/constants/date';
import NoResultsPage from '../../components/no-results-page/NoResultsPage';
import { history } from '../../core/state/history';
import EditUserStatusModal from './modals/edit-user-status/EditUserStatusModal';
import { isReadAllRole } from '../../core/state/user/selectors';

import './ExternalUsers.scss';

interface Props {
  pageType: number;
}

const ExternalUsers: React.FC<Props> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const users = useSelector((state: RootState) => state.users.externalUsers);
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const isUserReader = useSelector(isReadAllRole);

  React.useEffect(() => {
    dispatch(getExternalUsers());
  }, [dispatch]);

  return (
    <TableWrapper>
      <div className="table__header">
        <span>{t('externalUsers.title')}</span>
        <div>
          {!isUserReader && (
            <Button onClick={() => setIsModalOpen(true)} className="external-users__edit" buttonTypes={['blue-outline', 'small']}>
              <span>{t('common.editStatus')}</span>
            </Button>
          )}
        </div>
      </div>
      <div className="external-users__table">
        <Table>
          <THead>
            <TR>
              <TH>{t('common.companyName')}</TH>
              <TH>{t('common.email')}</TH>
              <TH>{t('common.mobileNumber')}</TH>
              <TH>{t('common.createdOn')}</TH>
              <TH>{t('common.status')}</TH>
            </TR>
          </THead>
          <TBody>
            {users?.map((user) => (
              <TR className="external-users__user" key={user.id} onClick={() => history.push(`/external-users/${user.id}/contacts`)}>
                <TD className="d-flex align-items-center">
                  <div className="external-users__user-img">
                    <Icon className="external-users__user-icon" icon="company" svg size="small" />
                  </div>
                  <span className="font-weight-bold">{user.name}</span>
                </TD>
                <TD className="text-left">{user.email}</TD>
                <TD className="text-left">{user.mobilePhoneNumber}</TD>
                <TD className="text-left">{format(new Date(user.createdOn), DATE_FORMAT_EU_DOT)}</TD>
                <TD className="text-left">
                  <div className={`external-users__user-status ${!user.active ? 'external-users__user-status--blocked' : ''}`}>
                    {!user.active ? t('externalUsers.status.blocked') : t('externalUsers.status.unblocked')}
                  </div>
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
        {!users && (
          <NoResultsPage
            title={t('externalUsers.noResults.title')}
            description={t('externalUsers.noResults.description')}
            icon="company"
            iconClass="external-users__no-results-icon"
          />
        )}
      </div>
      {isModalOpen && <EditUserStatusModal isOpen={isModalOpen} closeModal={() => setIsModalOpen(false)} />}
    </TableWrapper>
  );
};

export default ExternalUsers;
